import { useEffect, useRef, useState, useCallback } from "react";
import ApiService from "../../api/ApiService";

/**
 * useApiRequest - Custom hook to initiate and track an API request using ApiService.
 * @param {object} config - Axios-like config: { url, method, data, params, headers }
 * @param {boolean} auto - If true, request is sent on mount or config change.
 * @returns {object} { data, error, status, meta, send, abort }
 */
const useApiRequest = (config, auto = false) => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle");
  const requestKeyRef = useRef(null);

  // console.log("Auto in the useApiRequest:", auto);

  const send = useCallback(
    (overrideConfig = {}) => {
      console.log("[useApiRequest] send() called", { config, overrideConfig });
      setStatus("pending");
      setError(null);
      const mergedConfig = { ...config, ...overrideConfig };
      console.log("[useApiRequest] Merged Config:", mergedConfig);
      const metaObj = ApiService.request(mergedConfig);
      console.log("Setting meta in useApiRequest:", metaObj, typeof metaObj, metaObj instanceof Promise);
      setMeta(metaObj);
      setStatus(metaObj.status);
      requestKeyRef.current = ApiService._getRequestKey(
        mergedConfig.url,
        mergedConfig.method || "get",
        mergedConfig.data,
        mergedConfig.params
      );
    },
    [config]
  );

  // Listen for status updates for this request
  useEffect(() => {
    const handler = ({ key, meta: metaObj, response, error: err }) => {
      if (key === requestKeyRef.current) {
        setMeta(metaObj);
        setStatus(metaObj.status);
        if (metaObj.status === "success" && response) setData(response.data);
        if (err) setError(err);
      }
    };
    ApiService.on("status", handler);
    return () => {
      ApiService.off("status", handler);
    };
  }, []);

  // Auto-send on mount/config change
  useEffect(() => {
    // console.log("useEffect of useApiRequest triggered with auto value", auto);
    if (auto) {
      console.log("[useApiRequest] auto-triggered send()", { config });
      send();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, send]);

  // Abort function
  const abort = useCallback(() => {
    if (requestKeyRef.current) {
      ApiService.abortRequest(requestKeyRef.current);
    }
  }, []);

  return { data, error, status, meta, send, abort };
};

export default useApiRequest;